import {Viewer, Worker} from '@react-pdf-viewer/core';
import {defaultLayoutPlugin} from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import {observer} from 'mobx-react-lite';
import Box from '@mui/material/Box';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import React, {useEffect, useRef} from 'react';
import {Typography} from '@mui/material';
import {MarketplaceDocumentViewerProps} from '../../../../entity/props/marketplace/marketplace-document-viewer-props';

import '../../../../assets/css/marketplace/marketplace-document-viewer.css';
import '../../../../assets/css/style.css';

const MarketplaceDocumentViewer = (props: MarketplaceDocumentViewerProps): JSX.Element => {
    const {documentUrl, documentName} = props;
    const containerRef = useRef<HTMLDivElement>(null);

    const pdfUrl = documentUrl || '';
    const pdfName = documentName || 'Select a document to preview';

    useEffect(() => {
        const hideToolbarItems = () => {
            if (!containerRef.current) return;

            const itemsToHide = [
                '[data-testid="core__toolbar-item-download"]',
                '[data-testid*="download"]',
                '[aria-label*="download"]',
                '[aria-label*="Download"]',
                '[title*="download"]',
                '[title*="Download"]',
                'button[aria-label*="download"]',
                'button[aria-label*="Download"]',
                'button[title*="download"]',
                'button[title*="Download"]',
                '[data-testid="core__toolbar-item-search"]',
                '[aria-label*="search"]',
                '[aria-label*="Search"]',
                '[title*="search"]',
                '[title*="Search"]',
                'button[aria-label*="search"]',
                'button[aria-label*="Search"]',
                '[data-testid="core__toolbar-item-full-screen"]',
                '[aria-label*="full screen"]',
                '[aria-label*="Full screen"]',
                '[title*="full screen"]',
                '[title*="Full screen"]',
                'button[aria-label*="full screen"]',
                'button[aria-label*="Full screen"]',
                '[data-testid="core__toolbar-item-rotate"]',
                '[aria-label*="rotate"]',
                '[aria-label*="Rotate"]',
                '[title*="rotate"]',
                '[title*="Rotate"]',
                'button[aria-label*="rotate"]',
                'button[aria-label*="Rotate"]',
                '[data-testid="core__toolbar-item-pager"]',
                '[data-testid="core__toolbar-item-theme"]',
                '[aria-label*="theme"]',
                '[aria-label*="Theme"]',
                '[aria-label*="dark mode"]',
                '[aria-label*="Dark mode"]',
                '[title*="theme"]',
                '[title*="Theme"]',
                '[title*="dark mode"]',
                '[title*="Dark mode"]',
                '[data-testid="core__toolbar-item-open"]',
                '[aria-label*="open file"]',
                '[aria-label*="Open file"]',
                '[title*="open file"]',
                '[title*="Open file"]',
                '[data-testid="core__toolbar-item-print"]',
                '[aria-label*="print"]',
                '[aria-label*="Print"]',
                '[title*="print"]',
                '[title*="Print"]',
                '[data-testid="core__toolbar-item-more"]',
                '[aria-label*="more"]',
                '[aria-label*="More"]',
                '[title*="more"]',
                '[title*="More"]',
                '[data-testid="core__sidebar-thumbnails"]',
                '[data-testid="core__sidebar-bookmarks"]',
                '[data-testid="core__sidebar-attachments"]',
                '[data-testid="core__sidebar-toggle"]',
                '[aria-label*="thumbnails"]',
                '[aria-label*="Thumbnails"]',
                '[aria-label*="bookmarks"]',
                '[aria-label*="Bookmarks"]',
                '[aria-label*="attachments"]',
                '[aria-label*="Attachments"]',
                '[aria-label*="sidebar"]',
                '[aria-label*="Sidebar"]',
                '[data-testid="core__toolbar-item-upload"]',
                '[data-testid="core__toolbar-item-share"]',
                '[data-testid="core__toolbar-item-settings"]',
                '[aria-label*="upload"]',
                '[aria-label*="share"]',
                '[aria-label*="settings"]'
            ];
            itemsToHide.forEach(selector => {
                const elements = containerRef.current!.querySelectorAll(selector);
                elements.forEach(element => {
                    const ariaLabel = element.getAttribute('aria-label') || '';
                    const dataTestId = element.getAttribute('data-testid') || '';

                    if (!ariaLabel.toLowerCase().includes('page') &&
                        !dataTestId.toLowerCase().includes('page-navigation-input') &&
                        !ariaLabel.toLowerCase().includes('zoom') &&
                        !dataTestId.toLowerCase().includes('zoom')) {
                        (element as HTMLElement).style.display = 'none';
                        (element as HTMLElement).style.visibility = 'hidden';
                        (element as HTMLElement).style.opacity = '0';
                        (element as HTMLElement).style.pointerEvents = 'none';
                    }
                });
            });

            const sidebarSelectors = [
                '.rpv-core__sidebar',
                '.rpv-core__sidebar-tabs',
                '.rpv-core__sidebar-tab',
                '.rpv-core__sidebar-content',
                '[data-testid="core__sidebar-tab-thumbnails"]',
                '[data-testid="core__sidebar-tab-bookmarks"]',
                '[data-testid="core__sidebar-tab-attachments"]',
                '[class*="sidebar"]',
                '[class*="Sidebar"]'
            ];
            sidebarSelectors.forEach(selector => {
                const elements = containerRef.current!.querySelectorAll(selector);
                elements.forEach(element => {
                    (element as HTMLElement).style.display = 'none';
                    (element as HTMLElement).style.visibility = 'hidden';
                    (element as HTMLElement).style.opacity = '0';
                    (element as HTMLElement).style.pointerEvents = 'none';
                    (element as HTMLElement).style.width = '0';
                    (element as HTMLElement).style.height = '0';
                    (element as HTMLElement).style.position = 'absolute';
                    (element as HTMLElement).style.left = '-9999px';
                    (element as HTMLElement).style.top = '-9999px';
                });
            });

            const sidebar = containerRef.current.querySelector('.rpv-core__sidebar');
            if (sidebar) {
                (sidebar as HTMLElement).style.display = 'none';
                (sidebar as HTMLElement).style.visibility = 'hidden';
                (sidebar as HTMLElement).style.opacity = '0';
                (sidebar as HTMLElement).style.pointerEvents = 'none';
                (sidebar as HTMLElement).style.width = '0';
                (sidebar as HTMLElement).style.height = '0';
                (sidebar as HTMLElement).style.position = 'absolute';
                (sidebar as HTMLElement).style.left = '-9999px';
                (sidebar as HTMLElement).style.top = '-9999px';
            }

            const innerPage = containerRef.current.querySelector('.rpv-core__inner-page');
            if (innerPage) {
                (innerPage as HTMLElement).style.marginLeft = '0';
                (innerPage as HTMLElement).style.paddingLeft = '0';
            }

            const pageLayer = containerRef.current.querySelector('.rpv-core__page-layer');
            if (pageLayer) {
                (pageLayer as HTMLElement).style.marginLeft = '0';
                (pageLayer as HTMLElement).style.paddingLeft = '0';
            }
        }

        hideToolbarItems();

        const timer = setTimeout(hideToolbarItems, 1000);
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && containerRef.current) {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE &&
                            containerRef.current!.contains(node as Element)) {
                            hideToolbarItems();
                        }
                    });
                }
            });
        });

        if (containerRef.current) {
            observer.observe(containerRef.current, {
                childList: true,
                subtree: true
            });
        }

        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, []);

    return (
        <Box className='marketplace-document-viewer-container' ref={containerRef}>
            <Box className={'view-document-container'}>
                <Box className={'document-box'}>
                    <PictureAsPdfIcon className='zitles-icon'/>
                    <Typography className={'view-document-name'}>{pdfName}</Typography>
                </Box>
            </Box>
            <Box className={'pdf-container'}>
                {
                    pdfUrl
                        ? <Worker workerUrl='https://unpkg.com/pdfjs-dist@3.7.107/build/pdf.worker.min.js'>
                            <Viewer fileUrl={pdfUrl} plugins={[defaultLayoutPlugin()]}/>
                          </Worker>
                        : <Box className={'pdf-placeholder'}>
                            <Typography variant='body2' color='text.secondary'>
                                No document selected. Please select a document from the sidebar to preview.
                            </Typography>
                          </Box>
                }
            </Box>
        </Box>
    );
}

export default observer(MarketplaceDocumentViewer);
